export enum ServiceManagementFeatures {
  BootstrapIntegrationConfiguration = "BootstrapIntegrationConfiguration",
  GetIntegrationLogEntries = "GetIntegrationLogEntries",
  GetIntegrationMMSConnectionStatus = "GetIntegrationMMSConnectionStatus",
  GetIntegrationMMSList = "GetIntegrationMMSList",
  GetIntegrationMMSListAll = "GetIntegrationMMSListAll",
  GetIntegrationMMSListTypes = "GetIntegrationMMSListTypes",
  GetIntegrationSecretConfiguration = "GetIntegrationSecretConfiguration",
  GetIntegrationWorkerList = "GetIntegrationWorkerList",
  GetIntegrationWorkerListAll = "GetIntegrationWorkerListAll",
  GetIntegrationWorkerListTypes = "GetIntegrationWorkerListTypes",
  GetIntegrationWorkerStatus = "GetIntegrationWorkerStatus",
  GetIntegrationWorkerStatusAll = "GetIntegrationWorkerStatusAll",
  GetServiceApiEnvironmentDetails = "GetServiceApiEnvironmentDetails",
  GetServiceConfigurationValues = "GetServiceConfigurationValues",
  GetServiceManagementMetadata = "GetServiceManagementMetadata",
  GetServiceOutboundIpDetails = "GetServiceOutboundIpDetails",
  ManageIntegrationPermissions = "ManageIntegrationPermissions",
  ManageIntegrationSecret = "ManageIntegrationSecret",
  PerformIntegrationMMSConnectionClose = "PerformIntegrationMMSConnectionClose",
  PerformIntegrationMMSConnectionTest = "PerformIntegrationMMSConnectionTest",
  PerformIntegrationWorkerStart = "PerformIntegrationWorkerStart",
  PerformIntegrationWorkerStop = "PerformIntegrationWorkerStop",
  UpdateConfiguration = "UpdateConfiguration",
  GetIntegrationMLSConnectionStatus = "GetIntegrationMLSConnectionStatus",
  PerformIntegrationMLSConnectionClose = "PerformIntegrationMLSConnectionClose",
  PerformIntegrationMLSConnectionTest = "PerformIntegrationMLSConnectionTest",
}

const serviceManagementFeatureDescriptions: Record<
  ServiceManagementFeatures,
  string
> = {
  [ServiceManagementFeatures.BootstrapIntegrationConfiguration]:
    "Creates or seeds this integration's configuration if the service supports bootstrap.",
  [ServiceManagementFeatures.GetIntegrationLogEntries]:
    "Intended to show recent service logs for this integration. This service does not implement log retrieval yet.",
  [ServiceManagementFeatures.GetIntegrationMMSConnectionStatus]:
    "Shows whether the membership connection to this venue is currently up.",
  [ServiceManagementFeatures.GetIntegrationMMSList]:
    "Fetches one membership lookup list. Choose the list type first.",
  [ServiceManagementFeatures.GetIntegrationMMSListAll]:
    "Fetches every membership lookup list this service supports.",
  [ServiceManagementFeatures.GetIntegrationMMSListTypes]:
    "Shows which membership lookup lists this service can return, such as gender or title.",
  [ServiceManagementFeatures.GetIntegrationSecretConfiguration]:
    "Shows which secrets this integration can store and whether each one is set. Does not reveal the secret values.",
  [ServiceManagementFeatures.GetIntegrationWorkerList]:
    "Fetches one worker lookup list. Choose the list type first.",
  [ServiceManagementFeatures.GetIntegrationWorkerListAll]:
    "Fetches every worker lookup list this service supports.",
  [ServiceManagementFeatures.GetIntegrationWorkerListTypes]:
    "Shows which worker lookup lists this service can return.",
  [ServiceManagementFeatures.GetIntegrationWorkerStatus]:
    "Shows the status of one background job. Enter the execution id first.",
  [ServiceManagementFeatures.GetIntegrationWorkerStatusAll]:
    "Shows the status of all background jobs for this integration.",
  [ServiceManagementFeatures.GetServiceApiEnvironmentDetails]:
    "Shows a snapshot of the running service: version, start time, environment, and loaded assemblies.",
  [ServiceManagementFeatures.GetServiceConfigurationValues]:
    "Shows the service configuration keys and values. Secret values are masked.",
  [ServiceManagementFeatures.GetServiceManagementMetadata]:
    "Shows what this service can do and the minimum role needed for each action.",
  [ServiceManagementFeatures.GetServiceOutboundIpDetails]:
    "Shows the public IP this service uses for outbound traffic, for venue firewall allowlists.",
  [ServiceManagementFeatures.ManageIntegrationPermissions]:
    "Permission to edit who can use this toolkit. Use the Permissions section to make changes.",
  [ServiceManagementFeatures.ManageIntegrationSecret]:
    "Permission to add, update, or delete secrets. Use the Integration Secret section to make changes.",
  [ServiceManagementFeatures.PerformIntegrationMMSConnectionClose]:
    "Closes the current membership session so the next request reconnects.",
  [ServiceManagementFeatures.PerformIntegrationMMSConnectionTest]:
    "Tries to connect to the venue membership system and reports success or failure.",
  [ServiceManagementFeatures.PerformIntegrationWorkerStart]:
    "Starts a background worker job for this integration.",
  [ServiceManagementFeatures.PerformIntegrationWorkerStop]:
    "Stops a running background worker job for this integration.",
  [ServiceManagementFeatures.UpdateConfiguration]:
    "Permission to save the integration configuration. Use the Configuration section to make changes.",
  [ServiceManagementFeatures.GetIntegrationMLSConnectionStatus]:
    "Shows whether the loyalty connection to this venue is currently up.",
  [ServiceManagementFeatures.PerformIntegrationMLSConnectionClose]:
    "Closes the current loyalty session so the next request reconnects.",
  [ServiceManagementFeatures.PerformIntegrationMLSConnectionTest]:
    "Tries to connect to the venue loyalty system and reports success or failure.",
};

export const getServiceManagementFeatureDescription = (
  featureName: string
): string | undefined => {
  return serviceManagementFeatureDescriptions[
    featureName as ServiceManagementFeatures
  ];
};

const allCapsAcronyms = ["MMS", "MLS"];

const titleCaseAcronyms: Record<string, string> = {
  Api: "API",
  Ip: "IP",
};

export const getServiceManagementFeatureDisplayName = (
  featureName: string
): string => {
  let working = featureName;
  const placeholders: Array<{ token: string; value: string }> = [];

  allCapsAcronyms
    .slice()
    .sort((a, b) => b.length - a.length)
    .forEach((acronym, index) => {
      if (!working.includes(acronym)) {
        return;
      }

      const token = `\0${index}\0`;
      working = working.split(acronym).join(token);
      placeholders.push({ token, value: acronym });
    });

  working = working
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");

  placeholders.forEach(({ token, value }) => {
    working = working.split(token).join(` ${value} `);
  });

  return working
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => titleCaseAcronyms[word] ?? word)
    .join(" ");
};

const manageFeaturePathSuffix: Partial<
  Record<ServiceManagementFeatures, string>
> = {
  [ServiceManagementFeatures.ManageIntegrationPermissions]: "permissions",
  [ServiceManagementFeatures.ManageIntegrationSecret]: "integration-secrets",
  [ServiceManagementFeatures.UpdateConfiguration]: "configuration",
};

const unimplementedServiceManagementFeatures = new Set<string>([
  ServiceManagementFeatures.GetIntegrationLogEntries,
]);

const hiddenServiceManagementFeatures = new Set<string>([
  ServiceManagementFeatures.GetIntegrationSecretConfiguration,
  ServiceManagementFeatures.GetIntegrationMMSListTypes,
  ServiceManagementFeatures.UpdateConfiguration,
]);

export const isServiceManagementFeatureHidden = (
  featureName: string
): boolean => {
  return (
    hiddenServiceManagementFeatures.has(featureName) ||
    featureName.includes("HybridConnection")
  );
};

const requiredParameterKey: Partial<Record<ServiceManagementFeatures, string>> =
  {
    [ServiceManagementFeatures.GetIntegrationMMSList]: "type",
    [ServiceManagementFeatures.GetIntegrationWorkerList]: "listTypeId",
    [ServiceManagementFeatures.GetIntegrationWorkerStatus]: "executionId",
  };

export const getServiceManagementFeatureManagePath = (
  featureName: string,
  businessId: string,
  integrationId: string,
  configurationTypeName?: string | null
): string | undefined => {
  const suffix =
    manageFeaturePathSuffix[featureName as ServiceManagementFeatures];
  if (!suffix) {
    return undefined;
  }

  const path = `/admin/businesses/${businessId}/integrations/${integrationId}/${suffix}`;
  if (
    featureName === ServiceManagementFeatures.UpdateConfiguration &&
    configurationTypeName
  ) {
    return `${path}/${configurationTypeName}`;
  }

  return path;
};

export const isServiceManagementFeatureRunnable = (
  featureName: string
): boolean => {
  return (
    !manageFeaturePathSuffix[featureName as ServiceManagementFeatures] &&
    !unimplementedServiceManagementFeatures.has(featureName)
  );
};

export const getServiceManagementFeatureRequiredParameterKey = (
  featureName: string
): string | undefined => {
  return requiredParameterKey[featureName as ServiceManagementFeatures];
};

export const getServiceManagementFeatureRequiredParameterLabel = (
  featureName: string
): string | undefined => {
  switch (featureName) {
    case ServiceManagementFeatures.GetIntegrationMMSList:
    case ServiceManagementFeatures.GetIntegrationWorkerList:
      return "a list type";
    case ServiceManagementFeatures.GetIntegrationWorkerStatus:
      return "an execution id";
    default:
      return undefined;
  }
};
